import {
  MessageSquare,
  BarChart3,
  TrendingUp,
  AlertTriangle,
  Quote,
  ArrowRight,
} from 'lucide-react';
import { Card } from '@/components/ui/card';
import useTranslation from '@/hooks/useTranslation';
import useResponsesPanelStore from '@/store/ResponsesPanelStore';
import { getCategoryColor } from '@/utils/categoriesColorList';
import type { Category } from '@/types';

interface QuestionSidebarProps {
  questionId: string;
  responseCount?: number;
  categories?: Category[];
  patternCount?: number;
  divergentCount?: number;
  quoteCount?: number;
}

function QuestionSidebar({
  questionId,
  responseCount,
  categories,
  patternCount,
  divergentCount,
  quoteCount,
}: QuestionSidebarProps) {
  const { lang } = useTranslation();
  const { open } = useResponsesPanelStore();

  const maxCount = categories?.reduce((max, cat) => Math.max(max, cat.count ?? 0), 0) || 1;

  return (
    <div className="flex flex-col gap-4">
      {/* Response stats */}
      {responseCount !== undefined && responseCount > 0 && (
        <Card className="border-neutral-200/80 shadow-sm rounded-xl p-5">
          <div className="flex items-center gap-2 mb-3">
            <BarChart3 className="h-4 w-4 text-neutral-400" />
            <h3 className="text-sm font-semibold text-neutral-900">{lang.get('msg.questionStats')}</h3>
          </div>
          <div className="flex flex-col gap-2">
            <StatRow
              icon={<MessageSquare className="h-3.5 w-3.5 text-primary" />}
              label={lang.get('msg.responses')}
              value={responseCount}
            />
            {patternCount !== undefined && patternCount > 0 && (
              <StatRow
                icon={<TrendingUp className="h-3.5 w-3.5 text-primary" />}
                label={lang.get('msg.patternsFound')}
                value={patternCount}
              />
            )}
            {divergentCount !== undefined && divergentCount > 0 && (
              <StatRow
                icon={<AlertTriangle className="h-3.5 w-3.5 text-chat-primary" />}
                label={lang.get('msg.divergentViews')}
                value={divergentCount}
              />
            )}
            {quoteCount !== undefined && quoteCount > 0 && (
              <StatRow
                icon={<Quote className="h-3.5 w-3.5 text-chat-primary" />}
                label={lang.get('msg.voiceQuotes')}
                value={quoteCount}
              />
            )}
          </div>
        </Card>
      )}

      {/* Compact category distribution */}
      {categories && categories.length > 0 && (
        <Card className="border-neutral-200/80 shadow-sm rounded-xl p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-neutral-900">{lang.get('msg.categories')}</h3>
            <span className="text-xs text-neutral-400">{categories.length}</span>
          </div>
          <div className="flex flex-col gap-2">
            {categories.slice(0, 8).map((cat, i) => (
              <button
                key={cat.label}
                type="button"
                onClick={() => open(questionId, { label: cat.label, value: cat.label })}
                className="group flex items-center gap-2 text-left"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-0.5">
                    <span className="text-xs text-neutral-600 truncate group-hover:text-neutral-900 transition-colors">
                      {cat.label}
                    </span>
                    <span className="text-xs text-neutral-400 shrink-0 ml-2">
                      {cat.count ?? 0}
                    </span>
                  </div>
                  <div className="h-1.5 w-full rounded-full bg-neutral-100 overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all group-hover:opacity-80"
                      style={{
                        width: `${((cat.count ?? 0) / maxCount) * 100}%`,
                        backgroundColor: getCategoryColor(i),
                      }}
                    />
                  </div>
                </div>
              </button>
            ))}
            {categories.length > 8 && (
              <span className="text-xs text-neutral-400">+{categories.length - 8} {lang.get('msg.nMore')}</span>
            )}
          </div>
        </Card>
      )}

      {/* View all responses — accent card. The primary path now lives
          as an inline `ResponsesZone` in the main column (first viewport),
          but many researchers will still want a fast "full filter view"
          shortcut from the sidebar. Styled as an accent card rather than
          a plain outline button so it reads as a purposeful action, not
          generic chrome. */}
      {responseCount !== undefined && responseCount > 0 && (
        <button
          type="button"
          onClick={() => open(questionId)}
          className="group flex items-center justify-between w-full text-left rounded-xl px-4 py-3 border border-primary/20 bg-gradient-to-br from-primary/5 to-primary/5/40 hover:from-primary/10/80 hover:to-primary/5 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/30"
        >
          <div className="flex items-center gap-2.5">
            <div className="flex items-center justify-center h-8 w-8 rounded-lg bg-white border border-primary/20 shadow-sm">
              <MessageSquare className="h-4 w-4 text-primary" />
            </div>
            <div className="flex flex-col">
              <span className="text-xs font-semibold text-primary">
                {lang.get('msg.viewAllResponses')}
              </span>
              <span className="text-[11px] text-primary/70">
                {responseCount} {responseCount === 1
                  ? lang.get('msg.response') || 'response'
                  : lang.get('msg.responses')}
              </span>
            </div>
          </div>
          <ArrowRight className="h-4 w-4 text-primary group-hover:translate-x-0.5 transition-transform" />
        </button>
      )}
    </div>
  );
}

function StatRow({ icon, label, value }: { icon: React.ReactNode; label: string; value: number }) {
  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2">
        {icon}
        <span className="text-xs text-neutral-600">{label}</span>
      </div>
      <span className="text-sm font-semibold text-neutral-900">{value}</span>
    </div>
  );
}

export default QuestionSidebar;
